import { observer } from "mobx-react";
import React, { FC, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  useTheme,
} from "@mui/material";

import { CloseIcon } from "../../icons";

import { useAppStore } from "../../../hooks/useAppStore";
import ApiKeysTable from "./settings/ApiKeysTable";
import ManageApiKeyDialog from "./settings/ManageApiKeyDialog";

interface SettingsApiKeyDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const SettingsApiKeyDialog: FC<SettingsApiKeyDialogProps> = observer(
  ({ isOpen, onClose }) => {
    const theme = useTheme();
    const appStore = useAppStore();
    const [managedApiKeyId, setManagedApiKeyId] = useState<string | null>(
      null,
    );

    useEffect(() => {
      if (isOpen) {
        appStore.loadApiKeySetting();
      }
    }, [isOpen, appStore]);

    const handleClose = () => {
      setManagedApiKeyId(null);
      onClose();
    };

    return (
      <>
        <Dialog
          open={isOpen}
          onClose={handleClose}
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
            },
          }}
        >
          <DialogTitle
            sx={{
              pb: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              fontWeight: 600,
              color: theme.palette.text.primary,
              fontSize: "19px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            Settings
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  "& svg": { width: 20, height: 20 },
                }}
              >
                <CloseIcon className="" />
              </Box>
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 3, py: 2 }}>
            <Box sx={{ pt: 2 }}>
              {appStore.isApiKeySettingLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : appStore.apiKeys.length > 0 ? (
                <ApiKeysTable
                  apiKeys={appStore.apiKeys}
                  onManage={setManagedApiKeyId}
                />
              ) : (
                <Alert severity="info" sx={{ width: "100%", maxWidth: "none" }}>
                  No API key has been provisioned for your account yet. Contact
                  your administrator to have one created.
                </Alert>
              )}
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              gap: 1.5,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button onClick={handleClose} variant="outlined" sx={{ ml: "auto" }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <ManageApiKeyDialog
          apiKeyId={managedApiKeyId}
          onClose={() => setManagedApiKeyId(null)}
        />
      </>
    );
  },
);

export default SettingsApiKeyDialog;
